import React, { useState, useEffect } from 'react';
import {
  getFirestore,
  collection,
  addDoc,
  doc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { app } from '../../firebase';
import ReactMarkdown from 'react-markdown';
import { ArrowLeft, Save, Eye, Edit3, Loader2 } from 'lucide-react';

const GRADIENTS = [
  { label: 'Cyan → Indigo', value: 'from-cyan-500/20 to-indigo-500/20' },
  { label: 'Indigo → Purple', value: 'from-indigo-500/20 to-purple-500/20' },
  { label: 'Emerald → Cyan', value: 'from-emerald-500/20 to-cyan-500/20' },
  { label: 'Rose → Orange', value: 'from-rose-500/20 to-orange-500/20' },
  { label: 'Violet → Fuchsia', value: 'from-violet-500/20 to-fuchsia-500/20' },
];

const CATEGORIES = [
  'AI & Healthcare',
  'Regulatory',
  'Innovation',
  'Medical Devices',
  'Digital Health',
  'Technology',
];

function slugify(text) {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

function estimateReadTime(content) {
  const words = content.trim().split(/\s+/).length;
  const mins = Math.max(1, Math.ceil(words / 200));
  return `${mins} min read`;
}

const emptyPost = {
  title: '',
  description: '',
  content: '',
  category: CATEGORIES[0],
  gradient: GRADIENTS[0].value,
  author: 'Dr. Julian Shapley',
  status: 'draft',
};

const AdminBlogEditor = ({ post, onBack }) => {
  const isEditing = !!post?.id;
  const [form, setForm] = useState(emptyPost);
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const db = getFirestore(app);

  useEffect(() => {
    if (post) {
      setForm({
        title: post.title || '',
        description: post.description || '',
        content: post.content || '',
        category: post.category || CATEGORIES[0],
        gradient: post.gradient || GRADIENTS[0].value,
        author: post.author || 'Dr. Julian Shapley',
        status: post.status || 'draft',
      });
    }
  }, [post]);

  const update = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleSave = async (publishNow) => {
    if (!form.title.trim() || !form.content.trim()) return;
    setSaving(true);

    const slug = slugify(form.title);
    const readTime = estimateReadTime(form.content);
    const status = publishNow ? 'published' : form.status;

    const data = {
      title: form.title.trim(),
      slug,
      description: form.description.trim(),
      content: form.content,
      category: form.category,
      gradient: form.gradient,
      author: form.author.trim(),
      readTime,
      status,
      updatedAt: serverTimestamp(),
      ...(status === 'published' && !post?.publishedAt
        ? { publishedAt: serverTimestamp() }
        : {}),
    };

    try {
      if (isEditing) {
        await updateDoc(doc(db, 'blog_posts', post.id), data);
      } else {
        data.createdAt = serverTimestamp();
        await addDoc(collection(db, 'blog_posts'), data);
      }
      setSaved(true);
      setTimeout(() => {
        setSaved(false);
        onBack();
      }, 800);
    } catch (err) {
      console.error('Save failed:', err);
    }
    setSaving(false);
  };

  const inputClass =
    'w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/40 focus:border-transparent transition-all';
  const labelClass = 'block text-sm font-semibold text-slate-300 mb-1.5';

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="p-2 rounded-lg hover:bg-white/[0.06] text-slate-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h2 className="text-2xl font-display font-bold text-white">
            {isEditing ? 'Edit Post' : 'New Post'}
          </h2>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setPreview(!preview)}
            className="flex items-center gap-2 px-4 py-2 rounded-xl border border-white/[0.08] text-slate-300 hover:bg-white/[0.04] text-sm font-medium transition-colors"
          >
            {preview ? <Edit3 className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            {preview ? 'Edit' : 'Preview'}
          </button>
          <button
            onClick={() => handleSave(false)}
            disabled={saving || !form.title.trim() || !form.content.trim()}
            className="flex items-center gap-2 px-4 py-2 rounded-xl border border-white/[0.08] text-slate-300 hover:bg-white/[0.04] text-sm font-medium transition-colors disabled:opacity-40"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Draft
          </button>
          <button
            onClick={() => handleSave(true)}
            disabled={saving || !form.title.trim() || !form.content.trim()}
            className="flex items-center gap-2 px-5 py-2 rounded-xl bg-gradient-to-r from-cyan-500 to-indigo-500 text-white font-semibold text-sm hover:opacity-90 transition-opacity disabled:opacity-40"
          >
            {saved ? 'Saved!' : 'Publish'}
          </button>
        </div>
      </div>

      {preview ? (
        /* ── Preview ── */
        <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-8">
          <span className="inline-block px-3 py-1 rounded-full bg-cyan-500/10 text-cyan-400 text-xs font-mono font-semibold uppercase tracking-wider">
            {form.category}
          </span>
          <h1 className="font-display text-4xl font-bold text-white leading-tight mt-4">
            {form.title || 'Untitled Post'}
          </h1>
          <p className="text-slate-400 mt-2">{form.description}</p>
          <div className="mt-8 prose prose-invert prose-lg max-w-none prose-headings:font-display prose-headings:font-bold prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4 prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3 prose-p:text-slate-300 prose-p:leading-relaxed prose-strong:text-white prose-a:text-cyan-400 prose-li:text-slate-300 prose-blockquote:border-cyan-500/40 prose-blockquote:text-slate-400">
            <ReactMarkdown>{form.content}</ReactMarkdown>
          </div>
        </div>
      ) : (
        /* ── Form ── */
        <div className="space-y-6">
          {/* Title */}
          <div>
            <label className={labelClass}>Title</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => update('title', e.target.value)}
              placeholder="Your blog post title…"
              className={inputClass}
            />
            {form.title && (
              <p className="text-xs text-slate-500 mt-1.5 font-mono">
                /blog/{slugify(form.title)}
              </p>
            )}
          </div>

          {/* Description */}
          <div>
            <label className={labelClass}>
              Description{' '}
              <span className="font-normal text-slate-500">
                — shows on the blog listing and in search results
              </span>
            </label>
            <textarea
              value={form.description}
              onChange={(e) => update('description', e.target.value)}
              placeholder="A brief summary of the post…"
              rows={2}
              className={inputClass + ' resize-none'}
            />
          </div>

          {/* Meta row */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className={labelClass}>Category</label>
              <select
                value={form.category}
                onChange={(e) => update('category', e.target.value)}
                className={inputClass}
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c} className="bg-[#0f172a]">
                    {c}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>Card Gradient</label>
              <select
                value={form.gradient}
                onChange={(e) => update('gradient', e.target.value)}
                className={inputClass}
              >
                {GRADIENTS.map((g) => (
                  <option key={g.value} value={g.value} className="bg-[#0f172a]">
                    {g.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>Author</label>
              <input
                type="text"
                value={form.author}
                onChange={(e) => update('author', e.target.value)}
                className={inputClass}
              />
            </div>
          </div>

          {/* Content */}
          <div>
            <label className={labelClass}>
              Content{' '}
              <span className="font-normal text-slate-500">— Markdown supported</span>
            </label>
            <textarea
              value={form.content}
              onChange={(e) => update('content', e.target.value)}
              placeholder={'## Introduction\n\nStart writing your post here…'}
              rows={20}
              className={inputClass + ' font-mono text-sm leading-relaxed resize-y'}
            />
            <p className="text-xs text-slate-500 mt-1.5">
              {estimateReadTime(form.content)} &middot;{' '}
              {form.content.trim().split(/\s+/).filter(Boolean).length} words
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBlogEditor;
